import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { motion } from "framer-motion";
import axios from "axios";
import { Activity, Eye, Users, FileText, Package, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

const iconMap = {
  view: Eye,
  lead: Users,
  content: FileText,
  inventory: Package,
  default: Activity,
};

export default function DashboardActivity() {
  const { getAuthHeader } = useAuth();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchActivity();
  }, []);

  const fetchActivity = async () => {
    try {
      const res = await axios.get(`${API}/dashboard/activity`, { headers: getAuthHeader() });
      setActivities(res.data?.activities || []);
    } catch (err) {
      console.error("Failed to fetch activity:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#7C3AED] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-activity">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-white font-['Outfit']">Activity Log</h1>
        <p className="text-[#94A3B8] mt-1">Track all actions and events on your account</p>
      </div>

      {activities.length > 0 ? (
        <div className="bg-[#0F172A] border border-white/10 rounded-xl divide-y divide-white/5">
          {activities.map((item, i) => {
            const Icon = iconMap[item.type] || iconMap.default;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.03 }}
                className="flex items-start gap-4 p-4"
              >
                <div className="w-9 h-9 rounded-lg bg-[#7C3AED]/20 flex items-center justify-center flex-shrink-0">
                  <Icon className="w-4 h-4 text-[#7C3AED]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm">{item.message}</p>
                  <p className="text-xs text-[#64748B] mt-1 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {item.time || (item.created_at ? new Date(item.created_at).toLocaleString() : "—")}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-[#0F172A] border border-white/10 rounded-xl p-12 text-center"
        >
          <Activity className="w-16 h-16 text-[#64748B] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">No Activity Yet</h2>
          <p className="text-[#94A3B8] max-w-md mx-auto">
            Your account activity will be tracked here — listing views, lead submissions, content updates, and more.
          </p>
        </motion.div>
      )}
    </div>
  );
}
